import { useState } from 'react'
import { FileText, ClipboardList, ExternalLink, Plus, Loader2, Trash2, ChevronLeft, ChevronRight, Pencil, DollarSign, Map } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useHomeStore } from '../store'
import type { PaginationState } from '../store'
import { useAuthStore } from '@store/authStore'
import { showToast } from '@store/uiStore'
import { draftsApi } from '@api/client'
import type { DealHubSession, HubSpotDeal } from '../types'
import type { Draft } from '@api/types'

// Discriminated union for table rows
export type TableRow =
  | { type: 'session'; data: DealHubSession }
  | { type: 'draft'; data: Draft }

interface SessionsTableProps {
  sessions: DealHubSession[]
  drafts?: Draft[]
  isLoading?: boolean
  title?: string
  emptyMessage?: string
  showDealName?: boolean
  onCreateSession?: () => void
  pagination?: PaginationState
  onPageChange?: (page: number) => void
  onDraftDeleted?: (draftId: number) => void
}

export function SessionsTable({
  sessions,
  drafts = [],
  isLoading = false,
  title = 'DealHub Sessions',
  emptyMessage = 'No sessions found',
  showDealName = true,
  onCreateSession,
  pagination,
  onPageChange,
  onDraftDeleted,
}: SessionsTableProps) {
  const navigate = useNavigate()
  const { selectDeal, deleteSession } = useHomeStore()
  const { user } = useAuthStore()
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deletingDraftId, setDeletingDraftId] = useState<number | null>(null)

  // Build merged rows: drafts first (most recent), then sessions
  const rows: TableRow[] = [
    ...drafts.map((d): TableRow => ({ type: 'draft', data: d })),
    ...sessions.map((s): TableRow => ({ type: 'session', data: s })),
  ]

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const handleSelectSession = (session: DealHubSession) => {
    const deal: HubSpotDeal = {
      id: session.id,
      hubspotDealId: session.hubspotDealId,
      name: session.hubspotDealName,
      amount: null,
      stage: 'Unknown',
      closeDate: null,
      ownerName: session.createdBy?.name || 'Unknown',
      companyName: session.companyName,
      createdAt: session.createdAt,
    }
    selectDeal(deal)
  }

  const handleOpenSession = (session: DealHubSession, module: 'quote' | 'scope' | 'roi', tab?: string) => {
    handleSelectSession(session)
    const tabParam = tab ? `&tab=${tab}` : ''
    navigate(`/${module}?session=${session.id}${tabParam}`)
  }

  const handleRowClick = (row: TableRow) => {
    if (row.type === 'draft') {
      const draft = row.data
      const path = draft.feature_type === 'quote' ? '/quote' : '/roi'
      navigate(`${path}?draft=${draft.id}`)
    } else {
      handleSelectSession(row.data)
    }
  }

  const handleDeleteSession = async (sessionId: string) => {
    const success = await deleteSession(sessionId)
    if (success) {
      showToast('Session deleted', 'success')
    } else {
      showToast('Failed to delete session', 'error')
    }
    setConfirmDeleteId(null)
  }

  const handleDeleteDraft = async (e: React.MouseEvent, draft: Draft) => {
    e.stopPropagation()
    if (!confirm(`Delete draft "${draft.title}"? This cannot be undone.`)) return
    setDeletingDraftId(draft.id)
    try {
      const res = await draftsApi.delete(draft.id)
      if (res.success) {
        showToast('Draft deleted', 'success')
        onDraftDeleted?.(draft.id)
      } else {
        showToast('Failed to delete draft', 'error')
      }
    } catch {
      showToast('Failed to delete draft', 'error')
    } finally {
      setDeletingDraftId(null)
    }
  }

  // Generate page numbers to display
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (!pagination || pagination.totalPages <= 1) return []
    const { currentPage, totalPages } = pagination
    const pages: (number | 'ellipsis')[] = []

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push('ellipsis')
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (currentPage < totalPages - 2) pages.push('ellipsis')
      pages.push(totalPages)
    }

    return pages
  }

  if (isLoading) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 size={24} className="text-primary animate-spin" />
          <span className="text-[var(--text-secondary)]">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-[var(--text-primary)]">{title}</h3>
          {pagination && pagination.totalSessions > 0 && (
            <span className="text-xs text-[var(--text-muted)] bg-[var(--bg-tertiary)] px-2 py-0.5 rounded-full">
              {pagination.totalSessions + drafts.length} total
            </span>
          )}
        </div>
        {onCreateSession && (
          <button
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            onClick={onCreateSession}
          >
            <Plus size={14} />
            New Session
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-[var(--text-muted)]">{emptyMessage}</p>
          {onCreateSession && (
            <button
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              onClick={onCreateSession}
            >
              <Plus size={16} />
              Create First Session
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[var(--bg-tertiary)] text-left">
                  <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">ID</th>
                  {showDealName && (
                    <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Name</th>
                  )}
                  <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Modules</th>
                  <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Contributors</th>
                  <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Last Modified</th>
                  <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {rows.map((row) => {
                  if (row.type === 'draft') {
                    const draft = row.data
                    return (
                      <tr
                        key={`draft-${draft.id}`}
                        className="hover:bg-[var(--bg-tertiary)]/50 transition-colors cursor-pointer"
                        onClick={() => handleRowClick(row)}
                      >
                        {/* ID + Draft badge */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-[var(--text-primary)]">D-{draft.id}</span>
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded bg-amber-500/15 text-amber-500 border border-amber-500/20">
                              Draft
                            </span>
                          </div>
                        </td>

                        {/* Name */}
                        {showDealName && (
                          <td className="px-4 py-4">
                            <div>
                              <div className="font-medium text-[var(--text-primary)]">{draft.title || 'Untitled'}</div>
                              {draft.customer_name && <div className="text-xs text-[var(--text-muted)]">{draft.customer_name}</div>}
                            </div>
                          </td>
                        )}

                        {/* Module — single icon for draft type */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${
                              draft.feature_type === 'quote'
                                ? 'bg-primary/20 text-primary'
                                : 'bg-emerald-500/20 text-emerald-500'
                            }`}>
                              {draft.feature_type === 'quote' ? <DollarSign size={16} /> : <FileText size={16} />}
                            </div>
                            <span className="text-xs text-[var(--text-muted)] capitalize">{draft.feature_type}</span>
                          </div>
                        </td>

                        {/* Contributors — draft author */}
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="w-7 h-7 rounded-full bg-amber-500/20 border-2 border-dark-secondary flex items-center justify-center">
                              <Pencil size={12} className="text-amber-500" />
                            </div>
                            <div className="ml-3 text-xs text-[var(--text-secondary)] hidden lg:block">
                              {draft.user_name || draft.user_email}
                            </div>
                          </div>
                        </td>

                        {/* Created */}
                        <td className="px-4 py-4">
                          <div className="text-sm text-[var(--text-primary)]">{formatDate(draft.created_at)}</div>
                          <div className="text-xs text-[var(--text-muted)]">by {draft.user_name?.split(' ')[0] || draft.user_email}</div>
                        </td>

                        {/* Last Modified */}
                        <td className="px-4 py-4">
                          <div className="text-sm text-[var(--text-primary)]">{formatDateTime(draft.updated_at)}</div>
                          <div className="text-xs text-[var(--text-muted)]">by {draft.user_name?.split(' ')[0] || draft.user_email}</div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => handleDeleteDraft(e, draft)}
                              disabled={deletingDraftId === draft.id}
                              className="p-2 text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                              title="Delete draft"
                            >
                              {deletingDraftId === draft.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  }

                  // Session row
                  const session = row.data
                  return (
                    <tr
                      key={`session-${session.id}`}
                      className="hover:bg-[var(--bg-tertiary)]/50 transition-colors cursor-pointer"
                      onClick={() => handleRowClick(row)}
                    >
                      {/* Session ID */}
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm text-[var(--text-primary)]">{session.id}</span>
                      </td>

                      {/* Deal Name */}
                      {showDealName && (
                        <td className="px-4 py-4">
                          <div>
                            <div className="font-medium text-[var(--text-primary)]">{session.hubspotDealName}</div>
                            {session.companyName && <div className="text-xs text-[var(--text-muted)]">{session.companyName}</div>}
                          </div>
                        </td>
                      )}

                      {/* Modules */}
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button
                            className={`p-1.5 rounded transition-colors ${
                              session.hasQuote
                                ? 'bg-primary/20 text-primary hover:bg-primary/30'
                                : 'bg-[var(--bg-tertiary)] text-gray-600 hover:bg-primary/10 hover:text-primary'
                            }`}
                            onClick={() => handleOpenSession(session, 'quote')}
                            title={session.hasQuote ? 'Open Quote' : 'Create Quote'}
                          >
                            <DollarSign size={16} />
                          </button>
                          <button
                            className={`p-1.5 rounded transition-colors ${
                              session.hasScope
                                ? 'bg-violet-500/20 text-violet-500 hover:bg-violet-500/30'
                                : 'bg-[var(--bg-tertiary)] text-gray-600 hover:bg-violet-500/10 hover:text-violet-500'
                            }`}
                            onClick={() => handleOpenSession(session, 'scope', 'blueprint')}
                            title={session.hasScope ? 'Open Blueprint' : 'Create Blueprint'}
                          >
                            <Map size={16} />
                          </button>
                          <button
                            className={`p-1.5 rounded transition-colors ${
                              session.hasScope
                                ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30'
                                : 'bg-[var(--bg-tertiary)] text-gray-600 hover:bg-amber-500/10 hover:text-amber-500'
                            }`}
                            onClick={() => handleOpenSession(session, 'scope', 'general')}
                            title={session.hasScope ? 'Open Scope' : 'Create Scope'}
                          >
                            <ClipboardList size={16} />
                          </button>
                        </div>
                      </td>

                      {/* Contributors */}
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="flex -space-x-2">
                            {session.contributors.slice(0, 3).map((contributor, index) => (
                              <div
                                key={contributor.id}
                                className="w-7 h-7 rounded-full bg-primary/20 border-2 border-dark-secondary flex items-center justify-center"
                                title={contributor.name}
                                style={{ zIndex: 3 - index }}
                              >
                                {contributor.picture ? (
                                  <img
                                    src={contributor.picture}
                                    alt={contributor.name}
                                    className="w-full h-full rounded-full"
                                  />
                                ) : (
                                  <span className="text-xs font-medium text-primary">
                                    {contributor.name.charAt(0)}
                                  </span>
                                )}
                              </div>
                            ))}
                            {session.contributors.length > 3 && (
                              <div
                                className="w-7 h-7 rounded-full bg-[var(--bg-tertiary)] border-2 border-dark-secondary flex items-center justify-center"
                                title={session.contributors
                                  .slice(3)
                                  .map((c) => c.name)
                                  .join(', ')}
                              >
                                <span className="text-xs text-[var(--text-secondary)]">+{session.contributors.length - 3}</span>
                              </div>
                            )}
                          </div>
                          <div className="ml-3 text-xs text-[var(--text-secondary)] hidden lg:block">
                            {session.contributors
                              .slice(0, 2)
                              .map((c) => c.name.split(' ')[0])
                              .join(', ')}
                            {session.contributors.length > 2 && ` +${session.contributors.length - 2}`}
                          </div>
                        </div>
                      </td>

                      {/* Created */}
                      <td className="px-4 py-4">
                        <div className="text-sm text-[var(--text-primary)]">{formatDate(session.createdAt)}</div>
                        <div className="text-xs text-[var(--text-muted)]">by {session.createdBy?.name?.split(' ')[0] || 'Unknown'}</div>
                      </td>

                      {/* Last Modified */}
                      <td className="px-4 py-4">
                        <div className="text-sm text-[var(--text-primary)]">{formatDateTime(session.lastModifiedAt)}</div>
                        <div className="text-xs text-[var(--text-muted)]">by {session.lastModifiedBy?.name?.split(' ')[0] || 'Unknown'}</div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button
                            className="p-2 text-[var(--text-muted)] hover:text-primary hover:bg-[var(--bg-tertiary)] rounded transition-colors"
                            title="Open in HubSpot"
                          >
                            <ExternalLink size={16} />
                          </button>
                          {user?.permissions?.canDeleteSessions && (
                            confirmDeleteId === session.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDeleteSession(session.id)}
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  className="px-2 py-1 text-xs bg-[var(--bg-tertiary)] text-[var(--text-secondary)] rounded hover:text-[var(--text-primary)] transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteId(session.id)}
                                className="p-2 text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete session"
                              >
                                <Trash2 size={16} />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && onPageChange && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-color)]">
              <div className="text-xs text-[var(--text-muted)]">
                Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1}–{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalSessions)} of {pagination.totalSessions}
              </div>

              <div className="flex items-center gap-1">
                {/* Previous */}
                <button
                  onClick={() => onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((page, idx) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="px-1 text-xs text-[var(--text-muted)]">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => onPageChange(page)}
                      className={`min-w-[28px] h-7 px-1.5 rounded text-xs font-medium transition-colors ${
                        page === pagination.currentPage
                          ? 'bg-primary text-white'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                {/* Next */}
                <button
                  onClick={() => onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
